import React from 'react';

import type { RailBlock } from '../hooks/useSectionRail';

import { LegendBlock, LegendKey } from './Legend';
import Spinner from './Spinner';
import { buildPagePath } from '../utils/contentPaths';

/**
 * The Knowledge centre, beside a content page rather than on the landing.
 *
 * Same device, same components: one bounded legend per group, two keyed columns
 * inside it. That is the point of it being the same — a member who has read the
 * landing's Knowledge centre already knows how to read this, and the rail is not
 * a second navigation vocabulary to learn beside the top bar.
 *
 * It exists because the top bar deliberately stops at a section's direct
 * children (see `InfoHubMenu`'s `toItem`). Everything below that had to remain
 * reachable from somewhere that is not a URL you already know, and a rail that
 * holds still while you read is the right place for depth the bar refuses to
 * walk.
 */
const ReferenceRail: React.FC<{ label: string; blocks: RailBlock[]; loading: boolean }> = ({
  label,
  blocks,
  loading,
}) => (
  <nav aria-label={`${label} contents`} className="min-w-0">
    {/* The landing's own words for this block, repeated verbatim so the rail is
        recognisably the same thing in a different position. */}
    <h2 className="mb-6 flex h-6 items-center font-ui text-step--2 font-medium uppercase tracking-[0.18em] text-ink-muted">
      Knowledge centre
    </h2>

    {loading ? (
      <div className="py-16">
        <Spinner size="lg" />
      </div>
    ) : (
      <div className="flex flex-col gap-8">
        {blocks.map((block) => (
          <LegendBlock
            key={block.key}
            head={block.title}
            to={block.to ? buildPagePath(block.to) : undefined}
            // One column, not the landing's two. At rail width two columns leave
            // roughly 140px a title, and these are admin-authored page names —
            // "Videos on how to download postal and activity targets" — so nearly
            // every one of them truncated to two words.
            columns={1}
          >
            {block.children.map((child) => (
              <LegendKey
                key={child.id}
                to={buildPagePath(child.slugFull)}
                label={child.title}
                count={child.pageCount}
              />
            ))}
          </LegendBlock>
        ))}
      </div>
    )}
  </nav>
);

export default ReferenceRail;
